import { useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';
import {
  useDeleteExperiment,
  useExperimentsTranslations,
  useExperimentsUserPermission,
} from 'modules/experiments/hooks';
import { ExperimentsTableRow } from 'modules/experiments/types';
import { getExperimentActionMenuItems, getQueryVariables } from 'modules/experiments/utils';
import {
  useModalContext,
  useTableFacetContext,
  useTablePaginationContext,
  useTableSortContext,
} from 'modules/experiments/contexts';
import { fetchExperimentsTableDataRequest } from 'modules/experiments/ducks/actions';
import { DisabledTooltip } from 'modules/experiments/components/pages/CommonStyled';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { colors } from 'ui/theme';
import { MODAL_VARIANTS } from 'ui/modal';
import { Overflow } from 'ui/icons';
import { ActionMenu } from 'ui/actionMenu';
import { MENU_ALIGNMENTS } from 'ui/actionMenu/types';

import { StyledButton, StyledDisabledActionMenuWrapper } from './Styled';

type ExperimentActionMenuProps = {
  experiment: ExperimentsTableRow;
};

export const ExperimentActionMenu = ({ experiment }: ExperimentActionMenuProps) => {
  const {
    id,
    name,
    state: status,
    campaign,
    campaign: { type: campaignType, messageMedium },
  } = experiment;

  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const { viewOnlyPermission, viewAndDraftPermission } =
    useExperimentsUserPermission(messageMedium);
  const { search, selectedFilters, selectedTab } = useTableFacetContext();
  const { page, pageSize } = useTablePaginationContext();
  const { sortBy, sortDirection } = useTableSortContext();
  const { getExperimentLinkAttributes } = useExperimentTakeover();

  const queryVariables = getQueryVariables({
    selectedFilters,
    selectedTab,
    pageSize,
    page,
    sortBy,
    sortDirection,
    search,
  });

  // Modal
  const { showModal, hideModal } = useModalContext();
  const deleteExperiment = useDeleteExperiment({
    id,
    name,
    /**
     * We reload the table data even if there is a failure.
     * We do this because the delete request might have timed out
     * But, the BE might end up deleting the experiment after the time-out
     * The reload makes sure we have the latest snapshot.
     */
    onComplete() {
      dispatch(fetchExperimentsTableDataRequest(queryVariables));
      hideModal();
    },
  });

  const showDeleteModal = () => {
    showModal({
      variant: MODAL_VARIANTS.DELETE,
      id: 'delete-experiment',
      title: t('table.deleteModal.header'),
      body: (
        <Trans
          i18nKey="experiments:table.deleteModal.body"
          values={{ experimentName: name }}
          components={{ bold: <strong /> }}
        />
      ),
      buttonText: t('table.deleteModal.button'),
      inProgressButtonText: t('table.deleteModal.progress'),
      onConfirm() {
        deleteExperiment();
      },
    });
  };

  // Action Menu
  const experimentLinkAttributes = getExperimentLinkAttributes({
    source: ExperimentTakeoverSource.EXPERIMENT_ACTION_MENU_INDEX,
    experiment,
    campaign,
  });
  const actionMenuOptions = getExperimentActionMenuItems({
    viewAndDraftPermission,
    campaignType,
    status,
    experimentLinkAttributes,
    t,
    showDeleteModal,
  });
  const actionMenuPresent = !!actionMenuOptions.length;

  if (actionMenuPresent && viewOnlyPermission) {
    return (
      <StyledDisabledActionMenuWrapper>
        <DisabledTooltip tooltipContent={t('permissions.actionMenu')}>
          <StyledButton data-test="action-menu-trigger" isDisabled={true}>
            <Overflow color={colors.grey90} />
          </StyledButton>
        </DisabledTooltip>
      </StyledDisabledActionMenuWrapper>
    );
  }
  if (actionMenuPresent && !viewOnlyPermission) {
    return (
      <ActionMenu
        data-test={`action-menu-trigger-${id}`}
        align={MENU_ALIGNMENTS.RIGHT}
        options={actionMenuOptions}
        style={{ display: 'flex', justifyContent: 'flex-end' }}
        id={`experiment-overflow-menu-${experiment.id}`}
      >
        <StyledButton onClick={() => null}>
          <Overflow color={colors.grey90} />
        </StyledButton>
      </ActionMenu>
    );
  }

  return <></>;
};
